import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { fetchParkingById, addReview } from '../store/parkingSlice';

export default function ParkingDetailPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selected: parking, loading } = useSelector(s => s.parking);
  const { isAuthenticated } = useSelector(s => s.auth);
  const [activeTab, setActiveTab] = useState('info');
  const [review, setReview] = useState({ rating: 5, comment: '' });
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    dispatch(fetchParkingById(id));
  }, [dispatch, id]);

  if (loading || !parking) {
    return (
      <div style={{ paddingTop: '64px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚗</div>
          <p>Loading parking details...</p>
        </div>
      </div>
    );
  }

  const availPct = parking.totalSlots > 0 ? (parking.availableSlots / parking.totalSlots) * 100 : 0;
  const statusColor = availPct > 50 ? '#10b981' : availPct > 20 ? '#f59e0b' : '#ef4444';
  const images = parking.images?.length ? parking.images : [
    'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?w=800',
    'https://images.unsplash.com/photo-1573348722427-f1d6819fdf98?w=800',
  ];

  const handleReview = (e) => {
    e.preventDefault();
    dispatch(addReview({ id, data: review }));
    setReview({ rating: 5, comment: '' });
  };

  const tabStyle = (active) => ({
    padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
    background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
    color: active ? '#6366f1' : '#64748b',
    border: active ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
    transition: 'all 0.2s',
  });

  return (
    <div style={{ paddingTop: '64px', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Back */}
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
          fontSize: '14px', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          ← Back
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '32px' }}>
          {/* Left Column */}
          <div>
            {/* Image Gallery */}
            <div style={{ position: 'relative', borderRadius: '20px', overflow: 'hidden', marginBottom: '24px', height: '320px' }}>
              <img src={images[imgIdx]} alt={parking.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {images.length > 1 && (
                <div style={{ position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px' }}>
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setImgIdx(i)} style={{
                      width: i === imgIdx ? 24 : 8, height: 8, borderRadius: '4px',
                      background: i === imgIdx ? '#6366f1' : 'rgba(255,255,255,0.5)',
                      border: 'none', cursor: 'pointer', transition: 'all 0.3s',
                    }} />
                  ))}
                </div>
              )}
              <div style={{
                position: 'absolute', top: '16px', right: '16px',
                background: `${statusColor}20`, border: `1px solid ${statusColor}40`,
                borderRadius: '10px', padding: '6px 14px',
                color: statusColor, fontSize: '13px', fontWeight: '700',
              }}>
                {parking.availableSlots} slots available
              </div>
            </div>

            {/* Title & Rating */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h1 style={{ fontSize: '28px', fontWeight: '900', marginBottom: '8px' }}>{parking.name}</h1>
                  <p style={{ color: '#64748b', fontSize: '14px' }}>📍 {parking.address}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '32px', fontWeight: '900', color: '#6366f1' }}>₹{parking.pricePerHour}</div>
                  <div style={{ color: '#64748b', fontSize: '13px' }}>per hour</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ color: '#f59e0b', fontSize: '18px' }}>★</span>
                  <span style={{ fontWeight: '700', fontSize: '16px' }}>{parking.rating?.toFixed(1) || '4.0'}</span>
                  <span style={{ color: '#64748b', fontSize: '13px' }}>({parking.totalReviews || 0} reviews)</span>
                </div>
                {parking.is24Hours && <span style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', borderRadius: '8px', padding: '4px 12px', fontSize: '12px', fontWeight: '700' }}>24/7 Open</span>}
                {parking.isVerified && <span style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1', borderRadius: '8px', padding: '4px 12px', fontSize: '12px', fontWeight: '700' }}>✓ Verified</span>}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {['info', 'amenities', 'reviews'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} style={tabStyle(activeTab === tab)}>
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'info' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Parking Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {[
                      { label: 'Total Slots', value: parking.totalSlots },
                      { label: 'Available Now', value: parking.availableSlots },
                      { label: 'Vehicle Types', value: (parking.vehicleTypes || []).join(', ') || 'All' },
                      { label: 'Operating Hours', value: parking.is24Hours ? '24/7' : `${parking.openTime || '08:00'} - ${parking.closeTime || '22:00'}` },
                      { label: 'Price/Hour', value: `₹${parking.pricePerHour}` },
                      { label: 'Price/Day', value: parking.pricePerDay ? `₹${parking.pricePerDay}` : 'N/A' },
                    ].map((item, i) => (
                      <div key={i} style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
                        <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>{item.label}</div>
                        <div style={{ fontWeight: '700', fontSize: '15px' }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {parking.description && (
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>About</h3>
                    <p style={{ color: '#94a3b8', lineHeight: 1.7 }}>{parking.description}</p>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'amenities' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Amenities & Features</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {(parking.amenities || ['CCTV', 'Security Guard', 'Covered Parking']).map((a, i) => (
                      <span key={i} style={{
                        background: 'rgba(99,102,241,0.1)', color: '#a5b4fc',
                        border: '1px solid rgba(99,102,241,0.2)', borderRadius: '10px',
                        padding: '8px 16px', fontSize: '13px', fontWeight: '600',
                      }}>✓ {a}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'reviews' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {isAuthenticated && (
                  <form onSubmit={handleReview} style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '16px', padding: '24px', marginBottom: '16px',
                  }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Write a Review</h3>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      {[1,2,3,4,5].map(r => (
                        <button key={r} type="button" onClick={() => setReview({ ...review, rating: r })} style={{
                          background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer',
                          color: r <= review.rating ? '#f59e0b' : '#374151',
                        }}>★</button>
                      ))}
                    </div>
                    <textarea
                      value={review.comment}
                      onChange={e => setReview({ ...review, comment: e.target.value })}
                      placeholder="Share your experience..."
                      rows={3}
                      style={{
                        width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px',
                        color: '#fff', fontSize: '14px', outline: 'none', resize: 'vertical', marginBottom: '12px',
                      }}
                    />
                    <button type="submit" style={{
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      color: '#fff', border: 'none', borderRadius: '10px',
                      padding: '10px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                    }}>Submit Review</button>
                  </form>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(parking.reviews || []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No reviews yet. Be the first!</div>
                  ) : (
                    (parking.reviews || []).map((r, i) => (
                      <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px' }}>
                              {r.user?.name?.charAt(0) || 'U'}
                            </div>
                            <span style={{ fontWeight: '600', fontSize: '14px' }}>{r.user?.name || 'User'}</span>
                          </div>
                          <div style={{ color: '#f59e0b' }}>{'★'.repeat(r.rating)}</div>
                        </div>
                        <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.6 }}>{r.comment}</p>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Booking Card */}
          <div>
            <div style={{
              position: 'sticky', top: '80px',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: '20px', padding: '28px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '20px' }}>Book This Spot</h3>

              {/* Availability Bar */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>Availability</span>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: statusColor }}>{Math.round(availPct)}% free</span>
                </div>
                <div style={{ height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${availPct}%`, background: statusColor, borderRadius: '4px', transition: 'width 0.5s' }} />
                </div>
              </div>

              {/* Price Breakdown */}
              <div style={{ background: 'rgba(99,102,241,0.05)', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#94a3b8', fontSize: '13px' }}>Hourly Rate</span>
                  <span style={{ fontWeight: '700' }}>₹{parking.pricePerHour}/hr</span>
                </div>
                {parking.pricePerDay && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '13px' }}>Daily Rate</span>
                    <span style={{ fontWeight: '700' }}>₹{parking.pricePerDay}/day</span>
                  </div>
                )}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#94a3b8', fontSize: '13px' }}>Platform Fee</span>
                    <span style={{ fontWeight: '700', color: '#10b981' }}>FREE</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate(`/book/${id}`)}
                disabled={parking.availableSlots === 0}
                style={{
                  width: '100%', padding: '16px',
                  background: parking.availableSlots === 0 ? 'rgba(100,116,139,0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: parking.availableSlots === 0 ? '#64748b' : '#fff',
                  border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '800',
                  cursor: parking.availableSlots === 0 ? 'not-allowed' : 'pointer',
                  marginBottom: '12px',
                }}
              >
                {parking.availableSlots === 0 ? 'No Slots Available' : 'Book Now →'}
              </button>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => navigate('/map')}
                  style={{
                    flex: 1, padding: '10px', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px',
                    color: '#94a3b8', fontSize: '13px', cursor: 'pointer',
                  }}
                >
                  🗺️ View on Map
                </button>
              </div>

              <div style={{ marginTop: '20px', padding: '12px', background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '10px' }}>
                <p style={{ color: '#10b981', fontSize: '12px', textAlign: 'center' }}>
                  ✓ Free cancellation up to 30 minutes before booking
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}